import React, { Dispatch, SetStateAction, useState } from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { useMutation, useQuery } from '@betterment/js-runtime';
import { Box, Button, H1, Table } from '@betterment/samba-react';

import { AcatsModal } from './AcatsModal';
import Layout, { StyledCheckbox } from './Layout';

import { CoPilotSectionProvider, useCoPilotSectionState } from '../../contexts/CoPilotSectionContext';
import { Acat } from '../../services/api/types/co_pilot/acats';
import { ActionRequestParams, CoPilotResponseMeta } from '../../utils/co_pilot';

const FailedAcatsRow: React.FC<
  React.PropsWithChildren<{ failedAcat: Acat; setCurrentModalAcat: Dispatch<SetStateAction<Acat | null>> }>
> = ({ failedAcat, setCurrentModalAcat }) => {
  const { isItemSelected, handleSelectedItemChange } = useCoPilotSectionState<Acat>();
  const { id, clientName, amount } = failedAcat;

  return (
    <Table.Row>
      <Table.Cell>
        <Box css={{ display: 'flex', alignItems: 'center' }}>
          <StyledCheckbox
            name={id}
            id={id}
            role="checkbox"
            type="checkbox"
            className="checkboxInputBase"
            checked={isItemSelected(id)}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => handleSelectedItemChange(target.id)}
          />
          {clientName}
        </Box>
      </Table.Cell>
      <Table.Cell>{formatMoney(toMoney(amount))}</Table.Cell>
      <Table.Cell>
        Failed |
        <Button variant="textPrimary" onClick={() => setCurrentModalAcat(failedAcat)} css={{ pl: 'space.50' }}>
          View
        </Button>
      </Table.Cell>
    </Table.Row>
  );
};

const FailedAcatsTable: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { itemList, status, handleSelectAllChange, lastPage, loadMore } = useCoPilotSectionState<Acat>();
  const [currentModalAcat, setCurrentModalAcat] = useState<Acat | null>(null);

  return (
    <>
      {currentModalAcat && (
        <AcatsModal acat={currentModalAcat} closeHandler={() => setCurrentModalAcat(null)} status="failed" />
      )}
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <StyledCheckbox
                  name="selectAll"
                  id="selectAll"
                  role="checkbox"
                  type="checkbox"
                  className="checkboxInputBase"
                  checked={status === 'allSelected'}
                  onChange={() => handleSelectAllChange()}
                />
                <H1 variant="title100">Client</H1>
              </Box>
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>
              <H1 variant="title100">Amount</H1>
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>
              <H1 variant="title100">Status</H1>
            </Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {itemList?.map(failedAcat => (
            <FailedAcatsRow key={failedAcat.id} failedAcat={failedAcat} setCurrentModalAcat={setCurrentModalAcat} />
          ))}
          {status !== 'empty' && status !== 'fetchError' && !lastPage && (
            <Table.Row>
              <Table.Cell>
                <Box css={{ px: 'space.200', pb: 'space.300' }}>
                  <Button variant="textPrimary" onClick={() => loadMore()}>
                    Load more
                  </Button>
                </Box>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </>
  );
};

const FailedAcats: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { refetch } = useQuery<Acat[], CoPilotResponseMeta>(`/api/v1/co_pilot/failed_acats_transfer_updates`, {
    skip: true,
  });
  const [postDismissal] = useMutation<{}, ActionRequestParams>(
    '/api/v1/co_pilot/failed_acats_transfer_updates/dismissals'
  );

  return (
    <CoPilotSectionProvider
      fetch={refetch}
      initialOrder={[{ col: 'client_name', dir: 'desc' }]}
      postHandleDismissal={postDismissal}
    >
      <Layout
        activeTab="Failed ACATS"
        header="These clients have failed ACATS transfers that you may want to follow up on."
        subHeader="View details about each failed transfer or delete them."
      >
        <FailedAcatsTable />
      </Layout>
    </CoPilotSectionProvider>
  );
};

export default FailedAcats;
